import {
  showChoiceConfirmDialog,
  showCustomConfirmDialog,
} from "../../../../common/components/dialogs/AppConfirmDialog";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { getSuccessToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";

interface IDeregisterResponse {
  message?: string;
  waitingListUsersCount?: number;
}

/**
 * Asks staff, then removes a client from the event. For a client with an email it is one
 * question with three answers: remove without the email, remove and send the "your reservation
 * was cancelled" email, or keep them. A client without an email gets the plain yes / no
 * question, with no word about an email.
 */
export const useRemoveEventParticipant = (eventId?: number) => {
  const { mutateAsync } = useApiMutation<IDeregisterResponse>();

  const deregister = async (
    user: IPlannerEventUserModel,
    sendCancellationEmail: boolean
  ) => {
    let data: IDeregisterResponse;

    try {
      data = await mutateAsync({
        endpoint: `${endpointsMetadata.planner.deregisterFromEvent}?itemId=${user.id}&eventId=${eventId}&sendCancellationEmail=${sendCancellationEmail}`,
        method: "delete",
        globalIsLoading: true,
        invalidateKeys: [
          [endpointsMetadata.planner.getEvent],
          [endpointsMetadata.user.getUser],
          [endpointsMetadata.family.getFamily],
        ],
        disableSuccessToast: true,
      });
    } catch {
      // useApiMutation has already shown the error.
      return;
    }

    // Show the deletion message first
    if (data?.message) {
      getSuccessToast(data.message);
    }

    // Then show the waiting list notification if applicable
    const waitingListUsersCount = data?.waitingListUsersCount ?? 0;
    if (waitingListUsersCount > 0) {
      showCustomConfirmDialog(
        `<b>Изпратихме имейл</b> до <b>${waitingListUsersCount} ${
          waitingListUsersCount === 1 ? "потребител" : "потребителя"
        }</b> от листата на изчакващи, които ще бъдат уведомени, че се е освободило място.<br/><br/>`,
        undefined,
        () => {},
        "success",
        "Добре",
        null
      );
    }
  };

  const removeParticipant = (user: IPlannerEventUserModel) => {
    if (!user.email) {
      showCustomConfirmDialog(
        "Сигурни ли сте, че искате да премахнете потребителя от това събитие?",
        () => deregister(user, false),
        () => {},
        "warning",
        "Да",
        "Не"
      );
      return;
    }

    showChoiceConfirmDialog(
      <>
        Сигурни ли сте, че искате да премахнете <b>{user.name}</b> от това
        събитие?
        <br />
        <br />
        Можете да му изпратите и имейл, че резервацията му е отменена.
      </>,
      [
        { label: "Да, без имейл", onClick: () => deregister(user, false) },
        {
          label: "Да, изпрати имейл",
          design: "secondary",
          onClick: () => deregister(user, true),
        },
        { label: "Не", design: "outline" },
      ],
      "warning"
    );
  };

  return { removeParticipant };
};
